<script setup lang="ts">
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import { computed, getCurrentInstance, useSlots } from "vue"
import { Card, Icon } from "@/components/ui"

interface FormSectionProps {
  title?: string
  description?: string
  variant?: "card" | "divider"
  as?: "section" | "fieldset"
  columns?: 1 | 2
  icon?: IconKeys
  dense?: boolean
  borderTop?: boolean
}

const props = withDefaults(defineProps<FormSectionProps>(), {
  title: "",
  description: "",
  variant: "divider",
  as: "section",
  columns: 1,
  icon: undefined,
  dense: false,
  borderTop: true,
})

const slots = useSlots()
const instance = getCurrentInstance()
const sectionId = `form-section-${instance?.uid ?? "static"}`

const sectionTag = computed(() => props.as)
const titleTag = computed(() => (props.as === "fieldset" ? "legend" : "h2"))
const hasTitle = computed(() => Boolean(props.title || slots.title))
const hasDescription = computed(() => Boolean(props.description || slots.description))
const hasActions = computed(() => Boolean(slots.actions))
const hasHeader = computed(() => Boolean(props.icon || hasTitle.value || hasDescription.value || hasActions.value))
const descriptionId = computed(() => (hasDescription.value ? `${sectionId}-description` : undefined))
const wrapperClasses = computed(() => {
  if (props.variant !== "divider")
    return "m-0 min-w-0 border-0 p-0 flex flex-col"

  return [
    "m-0 min-w-0 border-0 p-0 lg:grid lg:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] lg:items-start lg:gap-x-10 lg:gap-y-0",
    props.borderTop ? "first:border-t-0 first:pt-0 border-t border-gray-100 pt-6 md:pt-8" : "",
  ]
})

const headerClasses = computed(() => {
  return [
    "flex min-w-0 flex-col gap-4",
    props.variant === "divider"
      ? "mb-6 border-b border-gray-100 pb-5 lg:mb-0 lg:border-r lg:border-b-0 lg:pr-8 lg:pb-0"
      : "mb-5",
  ]
})

const legendClasses = computed(() => {
  return [
    "m-0 min-w-0 p-0 text-lg font-bold font-heading md:text-xl",
    props.variant === "divider" ? "lg:pr-8" : "",
  ]
})

const fieldsetHeaderClasses = computed(() => {
  return props.variant === "divider"
    ? "mb-6 flex min-w-0 flex-col gap-4 border-b border-gray-100 pt-4 pb-5 lg:mb-0 lg:border-r lg:border-b-0 lg:pr-8 lg:pb-0"
    : "mb-5 flex min-w-0 flex-col gap-4 pt-3"
})

const contentClasses = computed(() => {
  return [
    "grid min-w-0",
    props.dense ? "gap-4" : "gap-6",
    props.columns === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1",
  ]
})
</script>

<template>
  <Card v-if="variant === 'card'" variant="shadow" border-radius="xl">
    <component :is="sectionTag" :class="wrapperClasses" :aria-describedby="descriptionId">
      <component :is="titleTag" v-if="as === 'fieldset' && hasTitle" :class="legendClasses">
        <slot name="title">
          {{ title }}
        </slot>
      </component>

      <div v-if="hasHeader && as === 'section'" :class="headerClasses">
        <div class="flex items-start gap-3 lg:flex-col lg:items-start lg:gap-4">
          <div v-if="icon" class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-footer-bg text-nav-active">
            <Icon :icon="icon" class="size-5" />
          </div>
          <div class="min-w-0 text-left">
            <component :is="titleTag" v-if="hasTitle" class="text-lg font-bold font-heading md:text-xl">
              <slot name="title">
                {{ title }}
              </slot>
            </component>
            <div v-if="hasDescription" :id="descriptionId" class="mt-1 text-sm text-gray-600">
              <slot name="description">
                {{ description }}
              </slot>
            </div>
          </div>
        </div>
        <div v-if="hasActions" class="mt-1 flex flex-wrap items-center gap-2">
          <slot name="actions" />
        </div>
      </div>

      <div v-else-if="hasHeader" :class="fieldsetHeaderClasses">
        <div class="flex items-start gap-3 lg:flex-col lg:items-start lg:gap-4">
          <div v-if="icon" class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-footer-bg text-nav-active" aria-hidden="true">
            <Icon :icon="icon" class="size-5" />
          </div>
          <div class="min-w-0 text-left">
            <div v-if="hasDescription" :id="descriptionId" class="text-sm text-gray-600">
              <slot name="description">
                {{ description }}
              </slot>
            </div>
          </div>
        </div>
        <div v-if="hasActions" class="flex flex-wrap items-center gap-2">
          <slot name="actions" />
        </div>
      </div>

      <div :class="contentClasses">
        <slot />
      </div>
    </component>
  </Card>

  <component :is="sectionTag" v-else :class="wrapperClasses" :aria-describedby="descriptionId">
    <component :is="titleTag" v-if="as === 'fieldset' && hasTitle" :class="legendClasses">
      <slot name="title">
        {{ title }}
      </slot>
    </component>

    <div v-if="hasHeader && as === 'section'" :class="headerClasses">
      <div class="flex items-start gap-3 lg:flex-col lg:items-start lg:gap-4">
        <div v-if="icon" class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-footer-bg text-nav-active">
          <Icon :icon="icon" class="size-5" />
        </div>
        <div class="min-w-0 text-left">
          <component :is="titleTag" v-if="hasTitle" class="text-lg font-bold font-heading md:text-xl">
            <slot name="title">
              {{ title }}
            </slot>
          </component>
          <div v-if="hasDescription" :id="descriptionId" class="mt-1 text-sm text-gray-600">
            <slot name="description">
              {{ description }}
            </slot>
          </div>
        </div>
      </div>
      <div v-if="hasActions" class="flex flex-wrap items-center gap-2">
        <slot name="actions" />
      </div>
    </div>

    <div v-else-if="hasHeader" :class="fieldsetHeaderClasses">
      <div class="flex items-start gap-3 lg:flex-col lg:items-start lg:gap-4">
        <div v-if="icon" class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-footer-bg text-nav-active" aria-hidden="true">
          <Icon :icon="icon" class="size-5" />
        </div>
        <div class="min-w-0 text-left">
          <div v-if="hasDescription" :id="descriptionId" class="text-sm text-gray-600">
            <slot name="description">
              {{ description }}
            </slot>
          </div>
        </div>
      </div>
      <div v-if="hasActions" class="flex flex-wrap items-center gap-2">
        <slot name="actions" />
      </div>
    </div>

    <div :class="contentClasses">
      <slot />
    </div>
  </component>
</template>
